import React, { useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import { useGetSalesByRegionQuery } from "Services/Admin/DashboardApiSlice";
import { FiMap, FiList, FiMaximize2, FiNavigation } from "react-icons/fi";
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { formatIndianNumber } from "Utilities/CurrencyFormatter";

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Coordinate Lookup for Regional Cities (Tamil Nadu focus)
const REGION_COORDS: Record<string, [number, number]> = {
  "Vellore": [12.9165, 79.1325],
  "Chennai": [13.0827, 80.2707],
  "Madurai": [9.9252, 78.1198],
  "Salem": [11.6643, 78.1460],
  "Coimbatore": [11.0168, 76.9558],
  "Kancheepuram": [12.8342, 79.7036],
  "Tiruchirappalli": [10.7905, 78.7047],
  "Tirunelveli": [8.7139, 77.7567],
  "Erode": [11.3410, 77.7172],
  "Thoothukudi": [8.7642, 78.1348],
  "Bangalore": [12.9716, 77.5946],
  "Pondicherry": [11.9416, 79.8083],
};

const DEFAULT_CENTER: [number, number] = [11.1271, 78.6569]; // Center of Tamil Nadu

function createPulseIcon(color: string, size: number) {
  return L.divIcon({
    className: 'custom-pulse-icon',
    html: `
      <div style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: 50%;
        box-shadow: 0 0 10px ${color};
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          position: absolute;
          width: 100%;
          height: 100%;
          background: ${color};
          border-radius: 50%;
          animation: pulse 2s infinite ease-out;
          opacity: 0.6;
        "></div>
      </div>
      <style>
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(3); opacity: 0; }
        }
      </style>
    `,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
}

function ChangeView({ center, zoom }: { center: [number, number], zoom: number }) {
  const map = useMap();
  
  // Force map to recalculate size when container dimensions are stable
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 400);
    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

interface SalesByRegionProps {
  fromDate: string;
  toDate: string;
  warehouse?: string;
  categoryId?: string;
  productId?: string;
  mockMultiplier?: number;
}

export const SalesByRegion: React.FC<SalesByRegionProps> = ({ 
  fromDate, 
  toDate, 
  warehouse, 
  categoryId, 
  productId,
  mockMultiplier = 1
}) => {
  const plant = process.env.REACT_APP_PLANT || "";
  
  const { data: regionData, isLoading } = useGetSalesByRegionQuery({
    plant, fromDate, toDate, warehouse, categoryId, productId
  }, { pollingInterval: 60000 });

  const regions = useMemo(() => {
    const rawData = regionData?.results || [];
    if (rawData.length === 0) return [];

    return rawData.map((res: any) => {
      const regionName = res.region || "Unknown";
      // Case-insensitive lookup for coordinates
      const normalizedKey = Object.keys(REGION_COORDS).find(
        key => key.toLowerCase() === regionName.toLowerCase()
      );
      
      return {
        name: regionName,
        revenue: res.totalRevenue || 0,
        coords: normalizedKey ? REGION_COORDS[normalizedKey] : [
            DEFAULT_CENTER[0] + (Math.random() - 0.5) * 2,
            DEFAULT_CENTER[1] + (Math.random() - 0.5) * 2
        ]
      };
    }).sort((a: any, b: any) => b.revenue - a.revenue);
  }, [regionData]);

  const maxRevenue = Math.max(...regions.map((r: any) => r.revenue), 1);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 xl:p-6 flex flex-col group overflow-hidden h-[400px]">
      <div className="flex justify-between items-center pb-3 border-b border-gray-50 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
            <FiMap className="text-sm" />
          </div>
          <h3 className="text-[15px] font-bold text-[#1B2559]">Sales by Region</h3>
        </div>
        <div className="flex items-center gap-2">
           <span className="text-[10px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-tighter shadow-sm border border-emerald-100 animate-pulse">Live</span>
           <button className="text-gray-400 hover:text-blue-500 transition-colors p-1 hover:bg-gray-50 rounded">
              <FiMaximize2 className="text-sm" />
           </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-4 min-h-0">
        {/* MAP CONTAINER - Optimized height for visual balance */}
        <div className="h-[180px] w-full relative rounded-xl overflow-hidden border border-gray-100 shadow-inner bg-blue-50/20">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-[1000]">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : regions.length === 0 ? (
             <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-gray-50/50 z-[1000]">
                <div className="text-3xl mb-2">📍</div>
                <p className="text-[13px] font-bold text-[#1B2559]">No regional data found</p>
                <p className="text-[11px] text-gray-400 mt-1 max-w-[140px]">Change filters to see sales distribution</p>
             </div>
          ) : null}

          <div className="absolute inset-0 z-[1]">
            <MapContainer 
              center={DEFAULT_CENTER} 
              zoom={6} 
              zoomControl={false} 
              attributionControl={false}
              scrollWheelZoom={true}
              dragging={true}
              doubleClickZoom={true}
              touchZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution=""
              />
              <ZoomControl position="bottomright" />
              <ChangeView center={DEFAULT_CENTER} zoom={6} />
              
              {regions.map((region: any, idx: number) => {
                const pulseSize = Math.max(14, (region.revenue / maxRevenue) * 35);
                return (
                  <Marker 
                    key={idx} 
                    position={region.coords} 
                    icon={createPulseIcon("#2563eb", pulseSize)}
                  >
                    <Popup className="premium-popup">
                      <div className="p-1 px-2 py-1.5">
                        <div className="flex items-center gap-1.5 mb-1.5 opacity-60">
                           <FiNavigation className="text-[10px]" />
                           <span className="text-[9px] font-black uppercase tracking-[0.15em]">Regional Insight</span>
                        </div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">Region</p>
                        <p className="text-[13px] font-black text-[#1B2559] mt-0.5 uppercase">{region.name}</p>
                        
                        <div className="mt-3 pt-3 border-t border-gray-50">
                           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">Total Revenue</p>
                           <p className="text-[17px] font-black text-blue-600 mt-0.5">₹{formatIndianNumber(region.revenue, 2)}</p>
                        </div>

                        <div className="mt-3 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${(region.revenue / maxRevenue) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* REGION LIST - Balanced for vertical stacking */}
      
        <div className="flex-1 w-full flex flex-col gap-2 overflow-y-auto scrollbar-hide pr-1 pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <style dangerouslySetInnerHTML={{ __html: `
            .scrollbar-hide::-webkit-scrollbar { display: none; }
          `}} />
          <div className="flex items-center gap-2 mb-1 px-1 mt-1 shrink-0">
            <FiList className="text-[10px] text-blue-500" />
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-tight">Regional Performance Rankings</span>
          </div>
          {regions.map((region: any, idx: number) => (
            <div key={idx} className="bg-white border border-gray-100 rounded-xl px-2.5 xl:px-3 py-3 hover:border-blue-200 transition-all hover:shadow-md cursor-default group/item relative overflow-hidden flex flex-col gap-2.5 min-h-[105px]">
              {/* Row 1: Region + Rank */}
              <div className="flex justify-between items-start gap-2 relative z-10 min-h-[32px]">
                <span className="min-w-0 flex-1 text-[11px] xl:text-[12px] font-black text-[#1B2559] uppercase tracking-tight leading-tight whitespace-normal break-words">
                  {region.name}
                </span>
                <span className="shrink-0 text-[10px] font-black text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 h-fit mt-0.5">
                  #{idx + 1}
                </span>
              </div>

              {/* Row 2: Revenue Label + Value (Three-column grid for absolute collision protection) */}
              <div className="relative z-10 grid grid-cols-[auto_1fr_auto] items-center gap-1 mt-auto px-0.5">
                 <span className="shrink-0 text-[8px] font-bold text-gray-400 uppercase tracking-widest leading-none">REVENUE</span>
                 <div className="flex-1"></div>
                 <p className="text-[13px] xl:text-[15px] font-black text-blue-600 leading-none whitespace-nowrap tabular-nums">
                   ₹{formatIndianNumber(region.revenue, 2)}
                 </p>
              </div>

              {/* Row 3: Progress Bar */}
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden relative z-10 shadow-inner mt-0.5 mx-0.5">
                <div 
                  className="h-full bg-blue-500 transition-all duration-1000 group-hover/item:bg-blue-600 rounded-full" 
                  style={{ width: `${(region.revenue / maxRevenue) * 100}%` }}
                ></div>
              </div>
              {/* Subtle background number */}
              <div className="absolute -right-2 -bottom-4 text-gray-50 text-5xl font-black select-none pointer-events-none group-hover/item:text-blue-50/50 transition-colors">
                {idx + 1}
              </div>
            </div>
          ))}
          {regions.length === 0 && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-50 h-20 rounded-xl mb-3"></div>
          ))}
        </div>
      </div>

      <style>{`
        .premium-popup .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
          border: 1px solid #f1f5f9 !important;
          padding: 0 !important;
        }
        .premium-popup .leaflet-popup-content {
          margin: 0 !important;
          width: 170px !important;
        }
        .leaflet-container {
          font-family: inherit !important;
        }
         .leaflet-bottom.leaflet-right {
          bottom: 10px !important;
          right: 6px !important;
        }
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 4px 10px rgb(0 0 0 / 0.15) !important;
          border-radius: 6px !important;
          overflow: hidden !important;
        }
        .leaflet-control-zoom a {
          background-color: white !important;
          color: #1e5eff !important;
          border-bottom: 1px solid #f8fafc !important;
          font-weight: 900 !important;
          width: 22px !important;
          height: 22px !important;
          line-height: 22px !important;
          font-size: 12px !important;
        }
        .leaflet-control-zoom a:hover {
          background-color: #f1f5f9 !important;
          color: #0046ff !important;
        }
      `}</style>
    </div>
  );
};
